/**
 * Client-public runtime configuration (analytics ids), resolved from the
 * same-origin `appConfig.getPublic` endpoint at page load — NOT baked into
 * the build. One static web image therefore serves every environment.
 *
 * The fetch is started by the inline bootstrap in `index.html` (so GTM can
 * load as early as possible) and shared here via `window.__publicConfig`;
 * this module only adds a fallback fetch for contexts where the inline
 * script didn't run (tests) and the `import.meta.env` overrides used in
 * local dev builds.
 */

declare global {
  interface Window {
    __publicConfig?: Promise<Record<string, string>>
  }
}

function fetchPublicConfig(): Promise<Record<string, string>> {
  return fetch('/api/trpc/appConfig.getPublic')
    .then((r) => r.json())
    .then((d: unknown) => {
      const result = (d as { result?: { data?: { json?: unknown } } })?.result
      const data = result?.data
      const json = (data as { json?: unknown })?.json ?? data
      return json && typeof json === 'object'
        ? (json as Record<string, string>)
        : {}
    })
    .catch(() => ({}))
}

let cached: Promise<Record<string, string>> | null = null

export function loadPublicConfig(): Promise<Record<string, string>> {
  if (typeof window === 'undefined') return Promise.resolve({})
  if (!cached) {
    cached = window.__publicConfig ?? fetchPublicConfig()
  }
  return cached
}

/** Resolve one key: local-dev `VITE_*` env wins, then runtime config. */
export async function publicConfigValue(
  key: string,
  envOverride: string | undefined,
): Promise<string> {
  if (envOverride) return envOverride
  const cfg = await loadPublicConfig()
  return cfg[key] ?? ''
}
